import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { ApiError } from '../../../api/errors';
import { useAuth } from '../../../auth/AuthProvider';
import { ROUTE42_ASSISTANT_NAME } from '../../../config/identity';
import { askAceForGuidance } from '../api/ace-guidance.api';
import { recordProductGuidanceEventBestEffort } from '../api/product-guidance-analytics.api';
import { buildApplicationContextHint, matchGuidanceRoute } from '../context/application-context';
import { collectNextBestActionSafeState } from '../context/next-best-action-context';
import { launchGuidedProductTour } from '../../product-tour/navigation/launch-product-tour';
import type { AceConversationTurn, AceGuidanceResponse, AceNextBestAction } from '../types/product-guidance';
import { ACE_ICON_CLASS } from '../visuals';

const QUICK_PROMPTS = ['What can I do here?', 'What should I do next?'] as const;
const MAX_ACE_CONTEXT_TURNS = 6;
const MAX_ACE_CONTEXT_ANSWER_LENGTH = 2000;

interface AcePanelTurn {
  question: string;
  result: AceGuidanceResponse;
}

export function AceGuidancePanel({ machineCount, machineId, machineName }: { machineCount: number | null; machineId: string | null; machineName: string | null }) {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turns, setTurns] = useState<AcePanelTurn[]>([]);
  const [pendingQuestion, setPendingQuestion] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const route = useMemo(() => matchGuidanceRoute(location.pathname), [location.pathname]);
  const conversationRoute = ['new-conversation', 'conversation'].includes(route?.id ?? '');
  const [mobileLauncherTarget, setMobileLauncherTarget] = useState<HTMLElement | null>(null);

  // Full-page Conversations have a persistent composer on phones. Rather than
  // floating Ace over the conversation content, dock the launcher into the
  // composer area itself. The portal is mobile-only, so desktop placement is
  // unchanged. A MutationObserver handles the brief machine-route loading state
  // where the composer slot may mount after the application shell.
  useEffect(() => {
    if (!conversationRoute || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      setMobileLauncherTarget(null);
      return;
    }

    const media = window.matchMedia('(max-width: 575px)');
    const syncTarget = () => {
      setMobileLauncherTarget(
        media.matches ? document.getElementById('r42-mobile-ace-launcher-slot') : null,
      );
    };

    syncTarget();
    media.addEventListener?.('change', syncTarget);
    const observer = typeof MutationObserver === 'function' ? new MutationObserver(syncTarget) : null;
    observer?.observe(document.body, { childList: true, subtree: true });

    return () => {
      media.removeEventListener?.('change', syncTarget);
      observer?.disconnect();
    };
  }, [conversationRoute, location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  useEffect(() => {
    setTurns([]);
    setPendingQuestion('');
    setError(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    window.setTimeout(() => {
      const body = bodyRef.current;
      if (body) body.scrollTop = body.scrollHeight;
    }, 0);
  }, [open, turns, pendingQuestion, busy, error]);

  async function ask(text: string) {
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    setPendingQuestion(trimmed);
    setQuestion('');
    try {
      const safeState = isWhatNextQuestion(trimmed) && auth.activeCustomerId
        ? await collectNextBestActionSafeState({
            activeCustomerId: auth.activeCustomerId,
            machineCount,
            machineId,
            pathname: location.pathname,
            permissions: auth.permissions,
          })
        : undefined;
      const contextHint = buildApplicationContextHint({
        pathname: location.pathname,
        organizationSelected: Boolean(auth.activeCustomerId),
        machineName,
        safeState,
      });
      const response = await askAceForGuidance({
        question: trimmed,
        context_hint: contextHint,
        ...(turns.length ? { conversation_context: { prior_turns: toConversationContext(turns) } } : {}),
      });
      setTurns((current) => [...current, { question: trimmed, result: response }].slice(-MAX_ACE_CONTEXT_TURNS));
      setPendingQuestion('');
    } catch (value) {
      setError(guidanceError(value));
    } finally {
      setBusy(false);
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    void ask(question);
  }

  function openProductTour() {
    const returnPath = `${location.pathname}${location.search}${location.hash}`;
    setOpen(false);
    launchGuidedProductTour(returnPath, navigate, 'ACE');
  }

  function continueInMachineIntelligence(response: AceGuidanceResponse, sourceQuestion: string) {
    const delegation = response.delegation;
    if (!delegation) return;
    const machineId = delegation.machine_id;
    if (!machineId) {
      navigate('/machines');
      setOpen(false);
      return;
    }
    if (delegation.recommended_route_id === 'conversation' && delegation.conversation_id) {
      navigate(`/machines/${machineId}/conversations/${delegation.conversation_id}`);
    } else {
      const original = encodeURIComponent(sourceQuestion);
      navigate(`/machines/${machineId}/conversations/new${original ? `?prompt=${original}` : ''}`);
    }
    setOpen(false);
  }

  const launcher = (
      <button className={`r42-ace-launcher${conversationRoute ? ' r42-ace-launcher--conversation' : ''}`} type="button" onClick={() => setOpen(true)} aria-haspopup="dialog" aria-expanded={open}>
        <span className="r42-ace-launcher__mark" aria-hidden="true"><i className={ACE_ICON_CLASS} /></span>
        <span>Ask {ROUTE42_ASSISTANT_NAME}</span>
      </button>
  );

  return (
    <>
      {mobileLauncherTarget ? createPortal(launcher, mobileLauncherTarget) : launcher}

      {open ? (
        <div className="r42-guidance-layer">
          <button className="r42-guidance-backdrop" type="button" aria-label={`Close ${ROUTE42_ASSISTANT_NAME}`} onClick={() => setOpen(false)} />
          <aside className="r42-guidance-panel" role="dialog" aria-modal="true" aria-labelledby="r42-guidance-title">
            <header className="r42-guidance-head">
              <div>
                <span className="r42-guidance-kicker">Route42 guide</span>
                <h2 id="r42-guidance-title">Ask {ROUTE42_ASSISTANT_NAME}</h2>
              </div>
              <button className="r42-icon-button" type="button" aria-label={`Close ${ROUTE42_ASSISTANT_NAME}`} onClick={() => setOpen(false)}>
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            </header>

            <div className="r42-guidance-context">
              <i className="bi bi-geo-alt" aria-hidden="true" />
              <span>{routeLabel(route?.id ?? null, machineName)}</span>
            </div>

            <div ref={bodyRef} className="r42-guidance-body">
              {turns.map((turn) => (
                <div className="r42-guidance-thread-turn" key={turn.result.request_id}>
                  <div className="r42-guidance-user-message" aria-label="Your question">
                    <span>You</span>
                    <p>{turn.question}</p>
                  </div>
                  <GuidanceResult
                    result={turn.result}
                    showSources={!isQuickPrompt(turn.question)}
                    onMachineIntelligence={() => continueInMachineIntelligence(turn.result, turn.question)}
                    onRecommendation={(recommendation) => {
                      recordProductGuidanceEventBestEffort({ event_type: 'RECOMMENDATION_CLICK', request_id: turn.result.request_id, recommendation_id: recommendation.id });
                      navigate(recommendationPath(recommendation, machineId, location.pathname));
                      setOpen(false);
                    }}
                    onNavigate={() => setOpen(false)}
                  />
                </div>
              ))}

              {pendingQuestion && (busy || error) ? (
                <div className="r42-guidance-user-message" aria-label="Your question">
                  <span>You</span>
                  <p>{pendingQuestion}</p>
                </div>
              ) : null}
              {!turns.length && !error && !busy ? (
                <section className="r42-guidance-welcome">
                  <span className="r42-ace-avatar" aria-hidden="true"><i className={ACE_ICON_CLASS} /></span>
                  <h3>How can I make this easier?</h3>
                  <p>I can explain Route42, answer general industrial questions, and help you think through the work. Machine-specific conclusions stay grounded in Machine Intelligence.</p>
                  <div className="r42-guidance-prompts">
                    {QUICK_PROMPTS.map((prompt) => (
                      <button key={prompt} type="button" disabled={busy} onClick={() => void ask(prompt)}>
                        <span>{prompt}</span><i className="bi bi-arrow-right" aria-hidden="true" />
                      </button>
                    ))}
                  </div>
                  <Link className="r42-guidance-investigation-action" to="/learn/investigations" onClick={() => setOpen(false)}>
                    <span><i className="bi bi-search" aria-hidden="true" /> Show me how Investigations work</span><i className="bi bi-arrow-right" aria-hidden="true" />
                  </Link>
                  <button className="r42-guidance-tour-action" type="button" onClick={openProductTour}>
                    <span className="r42-guidance-tour-action__icon" aria-hidden="true"><i className="bi bi-signpost-split" /></span>
                    <span className="r42-guidance-tour-action__copy"><strong>Show me how Route42 works</strong><span>Take a short guided example using a fictional machine.</span></span>
                    <i className="bi bi-arrow-right" aria-hidden="true" />
                  </button>
                </section>
              ) : null}

              {busy ? (
                <div className="r42-guidance-thinking" role="status" aria-live="polite">
                  <span className="r42-learning-dot" aria-hidden="true" />
                  <span>{ROUTE42_ASSISTANT_NAME} is working on that…</span>
                </div>
              ) : null}

              {error ? (
                <div className="r42-guidance-error" role="alert">
                  <i className="bi bi-exclamation-circle" aria-hidden="true" />
                  <div><strong>I couldn't open that guidance.</strong><span>{error}</span></div>
                </div>
              ) : null}

            </div>

            <footer className="r42-guidance-footer">
              <form onSubmit={submit} className="r42-guidance-composer">
                <input ref={inputRef} value={question} maxLength={500} disabled={busy} onChange={(event) => setQuestion(event.target.value)} placeholder="Ask about Route42 or industrial work…" aria-label={`Ask ${ROUTE42_ASSISTANT_NAME} about Route42 or industrial work`} />
                <button type="submit" disabled={busy || !question.trim()} aria-label="Send help question">
                  {busy ? <span className="spinner-border spinner-border-sm" aria-hidden="true" /> : <i className="bi bi-arrow-up" aria-hidden="true" />}
                </button>
              </form>
              <div className="r42-guidance-footer-links">
                <button type="button" onClick={openProductTour}><i className="bi bi-signpost-split" aria-hidden="true" /> Product Tour</button>
                <Link to="/help" onClick={() => setOpen(false)}>Browse the Help Center</Link>
              </div>
            </footer>
          </aside>
        </div>
      ) : null}
    </>
  );
}

function GuidanceResult({
  result,
  showSources,
  onMachineIntelligence,
  onRecommendation,
  onNavigate,
}: {
  result: AceGuidanceResponse;
  showSources: boolean;
  onMachineIntelligence: () => void;
  onRecommendation: (recommendation: AceNextBestAction) => void;
  onNavigate: () => void;
}) {
  const [feedback, setFeedback] = useState<'HELPFUL' | 'NOT_HELPFUL' | null>(null);
  const [feedbackReason, setFeedbackReason] = useState<string | null>(null);

  function rate(rating: 'HELPFUL' | 'NOT_HELPFUL') {
    setFeedback(rating);
    setFeedbackReason(null);
    recordProductGuidanceEventBestEffort({ event_type: 'ACE_FEEDBACK', request_id: result.request_id, rating });
  }

  function explainNotHelpful(reason: 'DID_NOT_ANSWER' | 'NOT_CLEAR' | 'INSTRUCTIONS_DID_NOT_MATCH' | 'NEXT_STEP_NOT_USEFUL') {
    setFeedbackReason(reason);
    recordProductGuidanceEventBestEffort({ event_type: 'ACE_FEEDBACK', request_id: result.request_id, rating: 'NOT_HELPFUL', reason });
  }

  return (
    <article className="r42-guidance-answer" aria-live="polite">
      <div className="r42-guidance-answer__identity"><span className="r42-ace-avatar" aria-hidden="true"><i className={ACE_ICON_CLASS} /></span><strong>{ROUTE42_ASSISTANT_NAME}</strong></div>
      <p>{result.answer.text}</p>
      {result.answer.steps.length ? <ol>{result.answer.steps.map((step) => <li key={step}>{step}</li>)}</ol> : null}
      {result.answer.why_it_matters ? <div className="r42-guidance-why"><strong>Why this helps</strong><span>{result.answer.why_it_matters}</span></div> : null}
      {result.answer.next_action ? <div className="r42-guidance-next"><span>Good next step</span><strong>{result.answer.next_action}</strong></div> : null}
      {result.recommendation ? (
        <button className="btn r42-btn-primary r42-guidance-recommendation-button" type="button" onClick={() => onRecommendation(result.recommendation!)}>
          {result.recommendation.label} <i className="bi bi-arrow-right" aria-hidden="true" />
        </button>
      ) : null}
      {result.mode === 'MACHINE_INTELLIGENCE' ? (
        <button className="btn r42-btn-primary r42-guidance-machine-button" type="button" onClick={onMachineIntelligence}>
          Continue in Machine Intelligence <i className="bi bi-arrow-right" aria-hidden="true" />
        </button>
      ) : null}
      {result.mode === 'INDUSTRIAL_GUIDANCE' ? (
        <div className="r42-guidance-basis" aria-label="Answer knowledge basis">
          <span>{result.knowledge_basis === 'CONTEXTUAL_INDUSTRIAL_GUIDANCE' ? 'Industrial guidance · current work context' : 'General industrial guidance'}</span>
          <p>{result.knowledge_basis === 'CONTEXTUAL_INDUSTRIAL_GUIDANCE' ? 'Ace can use the Route42 surface you are working in for context, but this is not a conclusion about the machine.' : 'This answer uses general industrial knowledge, not evidence from this machine.'}</p>
        </div>
      ) : null}
      {showSources && result.sources.length ? (
        <div className="r42-guidance-sources">
          <span>Based on Route42 Help</span>
          {result.sources.map((source) => <Link key={source.article_id} to={`/help/${source.article_id}`} onClick={() => { recordProductGuidanceEventBestEffort({ event_type: 'HELP_SOURCE_CLICK', request_id: result.request_id, article_id: source.article_id }); onNavigate(); }}>{source.title}</Link>)}
        </div>
      ) : null}
      <div className="r42-guidance-feedback">
        {feedback === null ? (
          <>
            <span>Was this helpful?</span>
            <div>
              <button type="button" aria-label="Yes, this guidance was helpful" onClick={() => rate('HELPFUL')}><i className="bi bi-hand-thumbs-up" aria-hidden="true" /> Yes</button>
              <button type="button" aria-label="No, this guidance was not helpful" onClick={() => rate('NOT_HELPFUL')}><i className="bi bi-hand-thumbs-down" aria-hidden="true" /> No</button>
            </div>
          </>
        ) : feedback === 'HELPFUL' ? (
          <span className="r42-guidance-feedback__thanks"><i className="bi bi-check-circle" aria-hidden="true" /> Thanks. This helps us improve Route42 guidance.</span>
        ) : feedbackReason ? (
          <span className="r42-guidance-feedback__thanks"><i className="bi bi-check-circle" aria-hidden="true" /> Thanks. We'll use that signal to improve this experience.</span>
        ) : (
          <>
            <span>What could be better?</span>
            <div className="r42-guidance-feedback__reasons">
              <button type="button" onClick={() => explainNotHelpful('DID_NOT_ANSWER')}>Didn't answer my question</button>
              <button type="button" onClick={() => explainNotHelpful('NOT_CLEAR')}>Not clear</button>
              <button type="button" onClick={() => explainNotHelpful('INSTRUCTIONS_DID_NOT_MATCH')}>Steps didn't match</button>
              <button type="button" onClick={() => explainNotHelpful('NEXT_STEP_NOT_USEFUL')}>Next step wasn't useful</button>
            </div>
          </>
        )}
      </div>
    </article>
  );
}

function routeLabel(routeId: string | null, machineName: string | null): string {
  const labels: Record<string, string> = {
    'customer-selection': 'Choosing an organization', 'machine-start': 'Getting started', machines: 'Machines', 'create-machine': 'Creating a machine',
    conversations: 'Conversations', 'new-conversation': 'New conversation', conversation: 'Machine conversation', investigations: 'Investigations', investigation: 'Investigation',
    documents: 'Documents', 'add-documents': 'Adding documents', document: 'Document details', 'machine-memory': 'Machine Memory', activity: 'Activity',
    'machine-entry': 'Machine', 'users-access': 'Users & access', profile: 'Profile', organization: 'Organization', billing: 'Billing',
  };
  const page = routeId ? labels[routeId] ?? 'Route42' : 'Route42';
  return machineName && routeId?.startsWith('machine') === false && !['users-access', 'profile', 'organization', 'billing', 'machines'].includes(routeId ?? '') ? `${page} · ${machineName}` : page;
}

function isQuickPrompt(value: string): boolean {
  return QUICK_PROMPTS.some((prompt) => prompt === value);
}

function toConversationContext(turns: AcePanelTurn[]): AceConversationTurn[] {
  return turns.slice(-MAX_ACE_CONTEXT_TURNS).map(({ question, result }) => ({
    question,
    answer: result.answer.text.slice(0, MAX_ACE_CONTEXT_ANSWER_LENGTH),
    mode: result.mode,
  }));
}

function isWhatNextQuestion(value: string): boolean {
  const text = value.trim().toLocaleLowerCase();
  return /^(what should i do next\??|what next\??|what do i do now\??|where should i start\??|where do i start\??)$/.test(text);
}

function recommendationPath(recommendation: AceNextBestAction, machineId: string | null, pathname: string): string {
  const machineBase = machineId ? `/machines/${encodeURIComponent(machineId)}` : '/machines';
  const routes: Record<string, string> = {
    machines: '/machines',
    'create-machine': '/machines/new',
    billing: '/settings/billing',
    'users-access': '/settings/users',
    profile: '/settings/profile',
    organization: '/settings/organization',
    documents: `${machineBase}/documents`,
    'add-documents': `${machineBase}/documents/add`,
    conversations: `${machineBase}/conversations`,
    'new-conversation': `${machineBase}/conversations/new`,
    investigations: `${machineBase}/investigations`,
    'machine-memory': `${machineBase}/memory`,
    activity: `${machineBase}/activity`,
  };
  if (recommendation.route_id === 'investigation' && /\/investigations\/[^/]+$/.test(pathname)) return pathname;
  if (recommendation.route_id === 'conversation' && /\/conversations\/[^/]+$/.test(pathname)) return pathname;
  if (recommendation.route_id === 'document' && /\/documents\/[^/]+$/.test(pathname)) return pathname;
  if (recommendation.id === 'ask-about-current-document') {
    const match = pathname.match(/\/documents\/([^/]+)$/);
    if (match?.[1]) return `${machineBase}/conversations/new?document=${encodeURIComponent(match[1])}`;
  }
  return routes[recommendation.route_id] ?? machineBase;
}

function guidanceError(value: unknown): string {
  if (value instanceof ApiError) return value.message;
  return 'Please try again. You can also browse the Help Center for step-by-step guidance.';
}

